import React, { useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { useRequiredUser } from "~/common/user";
import { RemoteLogErrorBoundary } from "~/components/common/remote-log-error-boundary";
import { Loader } from "~/components/ui/loader";
import { useIntlDocumentTitle } from "~/hooks/use-document-title";
import { useUserActionNotification } from "~/hooks/use-user-action-notification";
import type { NumericString } from "~/types/common";

import { BookWidget } from "./book-widget";
import { formatDateToYYYYMMDD } from "./calendar-utils";
import { useGetMeetingsExpertsQuery, useGetMeetingsPatientsQuery, useGetMonthSlotsQuery, useLazyGetDoctorMeetingsQuery, usePostMeetingMutation } from "./meetings-api";
import { MeetingsEmptyFallback, MeetingsErrorFallback } from "./meetings-shared";
import type { TCreateMeeting, TMeeting } from "./meetings-types";
import { useIsRedesignMeetings } from "./redesign-meetings-context";

/** Booking that already exists for this expert and start. A timed-out submit must not look like a failure. */
export function findBookedMeeting(meetings: TMeeting[], payload: TCreateMeeting): TMeeting | undefined {
  const start = Date.parse(payload.start_time);
  if (Number.isNaN(start)) {
    return undefined;
  }
  return meetings.find(
    (meeting) =>
      meeting.expert.account_id === payload.expert_id && Date.parse(meeting.start_time_utc) === start,
  );
}

export function MeetingBookPage() {
  useIntlDocumentTitle("meetings.book.title");

  const { expert_id } = useParams<{ expert_id: string }>();
  const expertQuery = useGetMeetingsExpertsQuery({ expert_id });

  return (
    <RemoteLogErrorBoundary component="meeting_book_page" fallbackRender={MeetingsErrorFallback} onReset={expertQuery.refetch}>
      <MeetingBookPageContent />
    </RemoteLogErrorBoundary>
  );
}

function MeetingBookPageContent() {
  const user = useRequiredUser();

  const { expert_id } = useParams<{ expert_id: NumericString }>();
  const history = useHistory();

  const userActionNotification = useUserActionNotification();

  const expertQuery = useGetMeetingsExpertsQuery({ expert_id });
  const patientsQuery = useGetMeetingsPatientsQuery();

  const [date, setDate] = useState(new Date());
  const slotsQuery = useGetMonthSlotsQuery({ expert_id, date: formatDateToYYYYMMDD(date) });

  const [postMeeting] = usePostMeetingMutation();
  const [loadDoctorMeetings] = useLazyGetDoctorMeetingsQuery();

  // The redesign wrapper supplies the card surface; the standalone legacy route supplies its own.
  const redesign = useIsRedesignMeetings();
  const className = redesign ? "" : "tw-rounded-3xl tw-bg-white tw-py-4 tw-shadow-sm";

  // `patientsQuery` MUST be part of both guards. BookWidget renders the step-3 form only when it has
  // BOTH `?time` and `patients`; without this, a slow or failing patients request left `patients`
  // undefined, so picking a time flipped the header to "step 3 / enter your details" while the body
  // silently fell back to the CALENDAR — the slot click looked like it did nothing.
  if (expertQuery.isLoading || slotsQuery.isLoading || patientsQuery.isLoading) {
    return (
      <div className={className}>
        <div className="tw-mx-auto tw-grid tw-min-h-[570px] tw-max-w-[700px] tw-place-items-center tw-bg-white">
          <Loader />
        </div>
      </div>
    );
  }

  if (expertQuery.isError) {
    return <MeetingsErrorFallback title="meetings.book.error" resetErrorBoundary={expertQuery.refetch} />;
  }

  if (slotsQuery.isError) {
    return <MeetingsErrorFallback title="meetings.book.error" resetErrorBoundary={slotsQuery.refetch} />;
  }

  if (patientsQuery.isError) {
    return <MeetingsErrorFallback title="meetings.book.error" resetErrorBoundary={patientsQuery.refetch} />;
  }

  const expert = expertQuery.data?.[0];
  const slots = slotsQuery.data ?? [];

  if (!expert) {
    return <MeetingsEmptyFallback title="meetings.book.not_found" description="meetings.book.not_found.description" link={{ to: "/pages/meetings", children: "meetings.tabs.new" }} />;
  }

  return (
    <div className={className}>
      <BookWidget
        expert={expert}
        user={user}
        patients={patientsQuery.data}
        slots={slots}
        slotsAreLoading={slotsQuery.isFetching}
        onMonthChange={setDate}
        onBookMeeting={async (data) => {
          try {
            return await postMeeting(data).unwrap();
          } catch (error) {
            try {
              const meetings = await loadDoctorMeetings(undefined, false).unwrap();
              const saved = findBookedMeeting(meetings, data);
              if (saved) {
                return saved;
              }
            } catch {
              // The booking request failed and the upcoming list could not confirm it.
            }
            throw error;
          }
        }}
        onBookMeetingSuccess={(meeting) => history.push(`/pages/meetings/booked/${meeting.id}/success`)}
        onBookMeetingError={() => userActionNotification.error("meetings.book.submit_error")}
      />
    </div>
  );
}
